import { useEffect } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client';
import { useSnapshot } from 'valtio';

import { GET_MY_DRAFT_CAR } from '../services/carService/queries';
import { carActions, carStore, EditingCarDraft } from '../../store/carStore';
import { authStore } from '../../store/authStore';

type GetMyDraftCarData = {
  getMyDraftCar: EditingCarDraft | null;
};

/**
 * Add-listing draft: prefer in-memory `carStore` (Flutter `addCarDraftProvider` parity).
 * If empty (e.g. refresh mid-flow), resume via lean owner-scoped `getMyDraftCar`.
 * Never call platform-wide GET_ALL_CARS / `cars()`.
 */
export function useEditingCarDraft() {
  const { editingCar: storeDraft, editingCarId } = useSnapshot(carStore);
  const auth = useSnapshot(authStore);
  const hasSession = auth.isAuthenticated && !!auth.tokens.accessToken;

  const { data: draftData } = useApolloQuery<GetMyDraftCarData>(GET_MY_DRAFT_CAR, {
    skip: !hasSession || !!editingCarId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  const queryDraft = draftData?.getMyDraftCar ?? null;

  useEffect(() => {
    if (queryDraft?.id && !editingCarId) {
      carActions.startEditing(queryDraft.id, queryDraft);
    }
  }, [queryDraft, editingCarId]);

  const editingCar = (storeDraft ?? queryDraft) as EditingCarDraft | null | undefined;

  return {
    editingCar,
    editingCarId: editingCarId ?? editingCar?.id ?? null,
    /** Draft resume is optional — never block add-listing on draft query failure. */
    ready: true,
  };
}
